import { ContentfulLinkedAsset } from "components/Contentful/ContentfulLinkedComponent";
import { Asset, Entry } from "contentful";
import React, { useState } from "react";
import Image from "components/Contentful/Image";
import styles from "./BrandHeader.module.scss";
import classNames from "classnames";
import PartnerCheckmark from "public/icons/partner-checkmark.svg";
import TiiDisplay from "components/TiiDisplay/TiiDisplay";
import { IexQuote } from "$gql/types.gen";
import QuoteInfo from "../Quote/QuoteInfo";
import Button from "components/core/Button/Button";
import { Media } from "components/Media/media";
import Link from "next/link";
import Markdown from "components/Markdown/Markdown";
import Countdown from "react-countdown";
import {
  BrandDetailDocument,
  BrandInvestment,
} from "@tiicker/util/lib/contentful/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { triggerVoteCTAClick } from "source/analytics-events";
import {
  faArrowUpRight,
  faStopwatch,
  faArrowRight,
} from "@fortawesome/sharp-light-svg-icons";
import { useRouter } from "next/router";
import Banner from "components/Banner/Banner";
import VotingBoxIcon from "public/icons/voting-box.svg";
import RightArrowGreen from "public/icons/right-arrow-green.svg";

type Props = {
  includes: { Asset: Asset[] };
  brandDetailFields?: BrandDetailDocument;
  heroAssetId?: string;
  logoAssetId?: string;
  hasPerks?: boolean;
  onPerksClick?: () => void;
  partner?: boolean;
  brandName: string;
  tickerSymbol: string;
  description?: string;
  quoteInfo?: IexQuote | null;
  iexSymbol?: string;
  currentUserHoldingCount: number;
  openTradeModal(): void;
  scrollToAbout(): void;
  brandInvestment?: Entry<BrandInvestment>;
};

const BrandHeader = (props: Props) => {
  const router = useRouter();
  const [votingButtonDisabled, setVotingButtonDisabled] = useState(true);
  const [countdownComplete, setCountdownComplete] = useState(false);
  const timerComplete = () => {
    setVotingButtonDisabled(false);
    setCountdownComplete(true);
  };
  const rerouteToVoting = (symbol: string, url: string) => {
    triggerVoteCTAClick(symbol);
    router.push(`${url}?utm_source=TiiCKER`);
  };
  return (
    <div className={styles.BrandHeader}>
      <div className={styles.BrandHeader__wrapper}>
        {props.heroAssetId && (
          <Media lessThan="md">
            <div className={styles.BrandHeader__fullImageContainer}>
              <ContentfulLinkedAsset
                includesList={props.includes}
                component={Image}
                imageOptions={{ fm: "jpg", fl: "progressive" }}
                className={styles.BrandHeader__heroImage}
                assetId={props.heroAssetId}
                removeLazy
              />
            </div>
          </Media>
        )}
        <div className="container">
          <div className={classNames(styles.BrandHeader)}>
            {props.heroAssetId && (
              <Media
                greaterThanOrEqual="md"
                className={styles.BrandHeader__heroFresnal}
              >
                <div className={styles.BrandHeader__imageContainer}>
                  <ContentfulLinkedAsset
                    includesList={props.includes}
                    component={Image}
                    imageOptions={{ fm: "jpg", fl: "progressive" }}
                    className={styles.BrandHeader__heroImage}
                    assetId={props.heroAssetId}
                    removeLazy
                  />
                </div>
              </Media>
            )}
            <div
              className={classNames(styles.BrandHeader__detailContainer, {
                [styles.BrandHeader__fullWidth]:
                  props.heroAssetId === undefined,
              })}
            >
              {props.logoAssetId && (
                <div className={styles.BrandHeader__logoContainer}>
                  <ContentfulLinkedAsset
                    includesList={props.includes}
                    component={Image}
                    className={styles.BrandHeader__logo}
                    assetId={props.logoAssetId}
                    removeLazy
                  />
                  {props.partner &&
                    props.brandDetailFields?.brandDetailOverflow?.fields
                      ?.b2iPartnerLogo === false && (
                      <div className={styles.BrandHeader__partner}>
                        <PartnerCheckmark
                          className={styles.BrandHeader__partnerCheckmark}
                        />
                        Partner
                      </div>
                    )}
                  {props.brandDetailFields?.brandDetailOverflow?.fields
                    ?.b2iPartnerLogo === true && (
                    <div
                      className={styles.BrandHeader__partnerB2i}
                      onClick={() => {
                        if (typeof window !== "undefined") {
                          const aboutSection = document.getElementById("About");
                          if (aboutSection) {
                            aboutSection.scrollIntoView({ behavior: "smooth" });
                          }
                        }
                      }}
                    >
                      <PartnerCheckmark
                        className={styles.BrandHeader__partnerCheckmark}
                      />
                      A
                      <img
                        className={styles.BrandHeader__partnerImage}
                        src="/images/b2i-logo-dark.png"
                      />
                      partner
                    </div>
                  )}
                </div>
              )}
              <div className={styles.BrandHeader__brandNameContainer}>
                <div className={styles.BrandHeader__brandName}>
                  <h1
                    className={classNames(
                      props.brandName.length > 24
                        ? styles.BrandHeader__longHeader
                        : styles.BrandHeader__header
                    )}
                  >
                    {props.brandName}
                  </h1>

                  <TiiDisplay text={props.tickerSymbol} type="dark" />
                </div>
              </div>
              {props.description && (
                <Markdown
                  className={classNames(styles.BrandHeader__description, {
                    [styles.BrandHeader__nonBrandWidth]:
                      props.heroAssetId === undefined,
                  })}
                  markdownText={props.description}
                />
              )}
              <div
                className={classNames(styles.BrandHeader__valueContainer, {
                  [styles.BrandHeader__nonBrandWidth]:
                    props.heroAssetId === undefined,
                })}
              >
                {props.quoteInfo && (
                  <QuoteInfo
                    quote={props.quoteInfo}
                    openTrade={props.openTradeModal}
                  />
                )}
              </div>
              <div className={styles.BrandHeader__viewPerksContainer}>
                {(() => {
                  const showShareholderVoting =
                    props.brandDetailFields?.brandDetailOverflow?.fields
                      ?.partnerVotingDate &&
                    props.currentUserHoldingCount !== 0;

                  if (showShareholderVoting) {
                    const votingDate =
                      props.brandDetailFields?.brandDetailOverflow?.fields
                        ?.partnerVotingDate;
                    return (
                      <div
                        className={styles.BrandHeader__secondaryButtonContainer}
                      >
                        <button
                          className={`${styles.BrandHeader__votingButton} ${
                            votingButtonDisabled
                              ? styles.BrandHeader__votingButton_disabled
                              : ""
                          }`}
                          disabled={votingButtonDisabled}
                          onClick={() => {
                            if (!votingButtonDisabled) {
                              rerouteToVoting(
                                props.tickerSymbol,
                                props.brandDetailFields?.brandDetailOverflow
                                  ?.fields?.partnerVotingUrl || "/"
                              );
                            }
                          }}
                        >
                          <VotingBoxIcon
                            className={classNames({
                              [styles.BrandHeader__votingButtonIcon]: true,
                              [styles.BrandHeader__votingButtonIconOff]:
                                !countdownComplete,
                            })}
                          />
                          <span
                            className={styles.BrandHeader__votingButtonText}
                          >
                            Vote Your Proxy
                          </span>
                          <RightArrowGreen
                            className={classNames({
                              [styles.BrandHeader__rightIcon]: true,
                              [styles.BrandHeader__rightIconOff]:
                                !countdownComplete,
                            })}
                          />
                        </button>
                        {votingDate && (
                          <p className={styles.BrandHeader__viewPerksTimer}>
                            <FontAwesomeIcon
                              icon={faStopwatch}
                              style={{ marginRight: "5px" }}
                            />
                            {!votingButtonDisabled ? (
                              "OPEN NOW!"
                            ) : (
                              <>
                                OPENS IN{" "}
                                <Countdown
                                  onComplete={timerComplete}
                                  date={new Date(votingDate)}
                                />
                              </>
                            )}
                          </p>
                        )}
                      </div>
                    );
                  } else if (props.hasPerks) {
                    return (
                      <Link href="#Perks">
                        <Button
                          size="large"
                          href=""
                          className={styles.BrandHeader__viewPerksButton}
                        >
                          View Available Perks
                        </Button>
                      </Link>
                    );
                  }

                  return null;
                })()}

                {props.brandInvestment && (
                  <div
                    className={styles.BrandHeader__investmentButtonContainer}
                  >
                    <button
                      className={styles.BrandHeader__investmentButton}
                      style={{
                        backgroundColor: "transparent",
                        borderColor:
                          props.brandInvestment.fields.fundingSection?.fields
                            ?.buttonHex,
                      }}
                      onClick={() => {
                        window.open(
                          props.brandInvestment?.fields.investUrl,
                          "_blank",
                          "noopener, noreferrer"
                        );
                      }}
                    >
                      {
                        props.brandInvestment.fields.fundingSection?.fields
                          ?.buttonCopy
                      }
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className={styles.BrandHeader__investmentIcon}
                      />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {props.brandDetailFields?.brandDetailOverflow?.fields
          ?.partnerQABanner && (
          <Banner
            bodyText={
              props.brandDetailFields?.brandDetailOverflow?.fields
                .partnerQABannerInformation?.fields.bodyText || ""
            }
            linkUrl={`/events/${props.brandDetailFields?.brandDetailOverflow?.fields.partnerQABannerInformation?.fields.linkUrl}`}
            linkText={
              props.brandDetailFields?.brandDetailOverflow?.fields
                .partnerQABannerInformation?.fields.linkText
            }
            qaStyleBanner={true}
            linkTarget={"_self"}
          />
        )}
      </div>
    </div>
  );
};

export default BrandHeader;
